import React, { useState, useEffect, useMemo } from 'react';
import './ScheduledContentComponent.scss';
import { getFormattedTime } from '../../epg/utils/shared/time';
import { getUpcomingEventsWithTeams } from '../../epg/utils/shared/events';
import { DAZN_LOGO_URL, getMatchupLabels } from '../../epg/utils/shared/matchup';

const LIGHT_BG_LUMINANCE_THRESHOLD = 0.5;
const BASE_FONT_SIZE = 185;
const TEAM_PANEL_WIDTH = 960; // 1920px / 2 panels
const OVERSCAN_H_PX = 96;    // 5vw of 1920px
const SAFE_TEXT_WIDTH = TEAM_PANEL_WIDTH - 2 * OVERSCAN_H_PX; // 768px


function sameContent(a, b) {
    return a.length === b.length && a.every((e, i) =>
        e.id === b[i].id
        && e.schedule_start === b[i].schedule_start
        && e.thumbnail === b[i].thumbnail
        && e.title === b[i].title
        && e.firstTeam?.logo === b[i].firstTeam?.logo
        && e.firstTeam?.display_name === b[i].firstTeam?.display_name
        && e.firstTeam?.screen_name === b[i].firstTeam?.screen_name
        && e.secondTeam?.logo === b[i].secondTeam?.logo
        && e.secondTeam?.display_name === b[i].secondTeam?.display_name
        && e.secondTeam?.screen_name === b[i].secondTeam?.screen_name
    );
}

function hexToRgb(hex) {
    if (!hex) return null;
    const clean = hex.replace(/^#/, '');
    if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(clean)) return null;
    const expandedHex = clean.length === 3
        ? clean.split('').map(c => c + c).join('')
        : clean;
    return {
        r: Number.parseInt(expandedHex.slice(0, 2), 16),
        g: Number.parseInt(expandedHex.slice(2, 4), 16),
        b: Number.parseInt(expandedHex.slice(4, 6), 16),
    };
}

function isLightBackground(hexColor) {
    if (!hexColor) return false;
    const rgb = hexToRgb(hexColor);
    if (!rgb) return false;
    const luminance = (rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114) / 255;
    return luminance > LIGHT_BG_LUMINANCE_THRESHOLD;
}


function watermarkName(team) {
    return team.screen_name || team.display_name;
}

function calcFontSize(displayName) {
    if (!displayName) return BASE_FONT_SIZE;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return BASE_FONT_SIZE;
    ctx.font = `italic 800 ${BASE_FONT_SIZE}px 'Barlow Condensed', 'Arial Narrow', sans-serif`;
    const maxWidth = displayName.split(' ').reduce((max, word) => {
        return Math.max(max, ctx.measureText(word).width);
    }, 0);
    if (!maxWidth || maxWidth <= SAFE_TEXT_WIDTH) return BASE_FONT_SIZE;
    return Math.floor(BASE_FONT_SIZE * SAFE_TEXT_WIDTH / maxWidth);
}

export default function ScheduledContentComponent() {
    const [upcomingEvents, setUpcomingEvents] = useState(getUpcomingEventsWithTeams);

    useEffect(() => {
        function onStateUpdate() {
            setUpcomingEvents(prev => {
                const next = getUpcomingEventsWithTeams();
                return sameContent(prev, next) ? prev : next;
            });
        }
        window.addEventListener('UpshowStateUpdate', onStateUpdate);
        return () => window.removeEventListener('UpshowStateUpdate', onStateUpdate);
    }, []);

    if (!upcomingEvents.length) return null;

    const event = upcomingEvents[0];
    const firstTeam = event.firstTeam;
    const secondTeam = event.secondTeam;
    const timeLabel = getFormattedTime(event.schedule_start, 'h:mm A');
    const subscriptionLogo = event.subscriptionType?.rectangle_logo_url ?? event.subscriptionType?.logo_url;

    const firstIsLight = isLightBackground(firstTeam.background_color);
    const secondIsLight = isLightBackground(secondTeam.background_color);

    const { firstLabel: firstTeamLabel, secondLabel: secondTeamLabel } = getMatchupLabels(firstTeam, secondTeam);

    return (
        <div className='scheduled-state'>
            <div className='scheduled-state__teams'>
                <TeamPanel
                    team={firstTeam}
                    displayName={watermarkName(firstTeam)}
                    isLight={firstIsLight}
                    modifier='first'
                />
                <TeamPanel
                    team={secondTeam}
                    displayName={watermarkName(secondTeam)}
                    isLight={secondIsLight}
                    modifier='second'
                />
            </div>

            <div className='scheduled-state__bar'>
                <img className='scheduled-state__everpass-logo' src={DAZN_LOGO_URL} alt='DAZN For Business' />
                <div className='scheduled-state__bar-info'>
                    <p className='scheduled-state__bar-label'>UPCOMING EVENT</p>
                    <p className='scheduled-state__bar-matchup'>
                        {firstTeamLabel} @ {secondTeamLabel} / Starts at {timeLabel}
                    </p>
                </div>
                {subscriptionLogo && <img className='scheduled-state__subscription-logo' src={subscriptionLogo} alt='' />}
            </div>
        </div>
    );
}

function TeamPanel({ team, displayName, isLight, modifier }) {
    const fontSize = useMemo(() => calcFontSize(displayName), [displayName]);

    return (
        <div
            className={`scheduled-state__team scheduled-state__team--${modifier}`}
            style={{ backgroundColor: team.background_color }}
        >
            <span
                className='scheduled-state__team-name'
                style={{
                    fontSize: `${fontSize}px`,
                    ...(isLight ? { color: '#555555', opacity: 0.2 } : undefined),
                }}
            >
                {displayName}
            </span>
            <img className='scheduled-state__team-logo' src={team.logo} alt={team.display_name} />
        </div>
    );
}
